/**
 * A time on screen — SPEC §3.6.
 *
 * Every stamp is Taipei and says so, and **the viewer's own clock appears on hover only**. Two zones
 * on every stamp is a page about time zones rather than a page about the day.
 *
 * The hover reading is filled from `Intl.DateTimeFormat().resolvedOptions().timeZone`, so it is the
 * viewer's real zone rather than a drawn guess — `boot.js`'s own mechanism, and `data-taipei` is kept
 * as the attribute name because the mockups and the size audit both look for it.
 */

import { useT } from "../../i18n/useT";
import { localReading, when } from "../../lib/time";
import { useCompetition } from "../../store/competition";

interface Props {
  at: string | null | undefined;
  /** No hover reading: for a stamp inside a table cell, where a tooltip per row is noise. */
  bare?: boolean;
}

export function Stamp({ at, bare }: Props) {
  const { t, lang } = useT();
  // `now` is the server's, from the heartbeat. A stamp from another day carries its weekday, and
  // whether it is another day is a question about the market's date, not the viewer's.
  const now = useCompetition((s) => s.heartbeat?.now) ?? "";
  if (!at) return <span className="muted">{t("ui.na")}</span>;

  const said = now ? when(at, now, lang) : at.slice(11, 16);
  if (bare) return <span className="num">{said}</span>;

  const local = localReading(at);
  return (
    <span className="tip num" data-taipei={at.slice(0, 16)} tabIndex={0}>
      {said}
      <span className="tip__body" role="tooltip">
        <span className="tip__local">{local ?? t("ui.na")}</span>
        <span className="tip__note">{t("ui.yourClock")}</span>
      </span>
    </span>
  );
}
